import React, { useCallback, useMemo } from 'react'
import { Form, TreeSelect as AntTreeSelect, Typography } from 'antd'
import produce from 'immer'
import { isUndefined, noop } from 'lodash'
import PropTypes from 'prop-types'

import {
  ALL_TREE_SELECT_OPTION_NODE,
  DROPDOWN_MIN_WIDTH,
} from '../../constants'
import { handleTreeSelectDropdownSearchHighlights } from '../../helpers'
import { antFormItemNamePropType } from '../../prop-types'
import {
  formatFormLabel,
  getFormItemLabelAboveConfiguration,
} from '../../utils'

const TreeSelect = ({
  name,
  treeData,
  onChange,
  value,
  treeCheckable,
  ...rest
}) => {
  const handleOnChange = useCallback(
    value => {
      onChange(isUndefined(value) ? null : value)
    },
    [onChange],
  )

  const maxTagPlaceholder = useCallback(
    omittedValues => `and ${omittedValues.length} more selected`,
    [],
  )

  return (
    <AntTreeSelect
      treeNodeLabelProp="namePath"
      onChange={handleOnChange}
      value={value}
      treeCheckable={treeCheckable}
      maxTagCount={2}
      maxTagPlaceholder={maxTagPlaceholder}
      dropdownRender={handleTreeSelectDropdownSearchHighlights}
      treeData={treeData}
      treeNodeFilterProp="title"
      showCheckedStrategy={treeCheckable ? AntTreeSelect.SHOW_ALL : undefined}
      dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
      {...rest}
    />
  )
}

TreeSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  name: antFormItemNamePropType.isRequired,
  treeData: PropTypes.array.isRequired,
  treeCheckable: PropTypes.bool,
}

TreeSelect.defaultProps = {
  value: undefined,
  onChange: noop,
  treeCheckable: false,
}

const FormTreeSelect = ({
  name,
  label,
  treeData,
  mode,
  rules,
  labelAbove,
  showAllOption,
  treeCheckable,
  showColon,
  noStyle,
  ...rest
}) => {
  const treeDataWithAll = useMemo(
    () =>
      showAllOption && treeCheckable
        ? produce(treeData, newOptions => [
            {
              ...ALL_TREE_SELECT_OPTION_NODE,
              children: newOptions,
            },
          ])
        : treeData,
    [showAllOption, treeCheckable, treeData],
  )

  return (
    <Form.Item
      labelCol={getFormItemLabelAboveConfiguration(labelAbove)}
      name={name}
      label={
        <Typography.Text>{formatFormLabel(label, showColon)}</Typography.Text>
      }
      rules={rules}
      noStyle={noStyle}
    >
      <TreeSelect
        name={name}
        treeData={treeDataWithAll}
        treeCheckable={treeCheckable}
        {...rest}
      />
    </Form.Item>
  )
}

FormTreeSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  treeData: PropTypes.array.isRequired,
  mode: PropTypes.string,
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  labelAbove: PropTypes.bool,
  showAllOption: PropTypes.bool,
  treeCheckable: PropTypes.bool,
  showColon: PropTypes.bool,
  noStyle: PropTypes.bool,
}

FormTreeSelect.defaultProps = {
  label: '',
  noStyle: false,
  mode: undefined,
  rules: undefined,
  labelAbove: false,
  showAllOption: false,
  treeCheckable: false,
  showColon: true,
}

export default FormTreeSelect
